import { useMemo, useEffect, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAtomValue } from 'jotai';
import { useStateReducer, showNotification } from 'r/utils';
import { hlFilteredPairsAtom } from 'r/atoms';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import type { CryptoPair } from 'r/types';
import { useUpdateAnalysis, useDeleteAnalysis } from '../mutations';

interface Analysis {
  id: string;
  pair: {
    symbol: string;
  };
  isPredictionUp: boolean;
  tweetData: {
    name: string;
    id: string;
    text: string;
  };
}

interface ModalAdminEditAnalysisProps {
  analysis: Analysis | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function ModalAdminEditAnalysis({
  analysis,
  isOpen: isOpenProp,
  onClose,
}: ModalAdminEditAnalysisProps) {
  const [isOpen, setIsOpen] = useState(isOpenProp);
  const pairs = useAtomValue(hlFilteredPairsAtom);
  const { mutateAsync: updateAnalysis } = useUpdateAnalysis();
  const { mutateAsync: deleteAnalysis } = useDeleteAnalysis();

  useEffect(() => {
    setIsOpen(isOpenProp);
  }, [isOpenProp]);

  const pairsPrepared = useMemo(
    () =>
      pairs.map((pair: CryptoPair) => ({
        ...pair,
        label: pair.symbol,
        icon: pair.logoUrl,
      })),
    [pairs],
  );

  const [{ pair, isDeleting }, ss] = useStateReducer({
    pair:
      analysis ?
        pairsPrepared.find((p: CryptoPair) => p.symbol === analysis.pair.symbol)
      : null,
    isDeleting: false,
  });

  useEffect(() => {
    if (analysis && isOpen) {
      ss({
        pair: pairsPrepared.find(
          (p: CryptoPair) => p.symbol === analysis.pair.symbol,
        ),
        isDeleting: false,
      });
    }
  }, [analysis, isOpen, pairsPrepared, ss]);

  const handlePairChange = async (value: CryptoPair) => {
    if (!analysis) return;

    try {
      await updateAnalysis({
        analysisId: analysis.id,
        body: { pair: value.symbol },
      });
      ss({ pair: value });
      showNotification('success', 'Trading pair updated successfully');
    } catch {
      showNotification('error', 'Failed to update trading pair');
    }
  };

  const handleDirectionChange = async (value: 'up' | 'down') => {
    if (!analysis || !pair) return;

    try {
      await updateAnalysis({
        analysisId: analysis.id,
        body: { isPredictionUp: value === 'up' },
      });
      showNotification('success', 'Prediction direction updated successfully');
    } catch {
      showNotification('error', 'Failed to update prediction direction');
    }
  };

  const handleDelete = async () => {
    if (!analysis) return;

    if (!isDeleting) {
      ss({ isDeleting: true });
      return;
    }

    try {
      await deleteAnalysis(analysis.id);
      handleClose();
    } catch {
      ss({ isDeleting: false });
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  if (!analysis) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Analysis</DialogTitle>
        </DialogHeader>

        <div className="text-sm text-secondary mb-6">
          <div>
            <strong>Author:</strong> {analysis.tweetData.name} (@
            {analysis.tweetData.id})
          </div>
          <div className="mt-1 line-clamp-2">
            <strong>Tweet:</strong> {analysis.tweetData.text}
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <Label className="text-sm font-medium mb-3 block">
              Trading Pair
            </Label>
            <Select
              value={pair?.symbol}
              onValueChange={(value) => {
                const selected = pairsPrepared.find((p) => p.symbol === value);
                if (selected) handlePairChange(selected);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select trading pair" />
              </SelectTrigger>
              <SelectContent>
                {pairsPrepared.map((p) => (
                  <SelectItem key={p.symbol} value={p.symbol}>
                    <div className="flex items-center gap-2">
                      {p.icon && (
                        <img src={p.icon} alt="" className="w-5 h-5" />
                      )}
                      {p.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-sm font-medium mb-3 block">
              Prediction Direction
            </Label>
            <RadioGroup
              value={analysis.isPredictionUp ? 'up' : 'down'}
              onValueChange={(value) =>
                handleDirectionChange(value as 'up' | 'down')
              }
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="up" id="up" />
                <Label htmlFor="up">Up</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="down" id="down" />
                <Label htmlFor="down">Down</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="pt-4 border-t">
            <h3 className="text-lg font-semibold text-red-500 mb-3">
              Danger Zone
            </h3>
            <div className="flex gap-4 bg-red-500/10 p-4 rounded-lg">
              <Button variant="destructive" onClick={handleDelete}>
                {isDeleting ?
                  'Click again to confirm DELETE'
                : 'DELETE ANALYSIS'}
              </Button>
              {isDeleting && (
                <Button
                  variant="outline"
                  onClick={() => ss({ isDeleting: false })}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6 pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
